import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosError, AxiosInstance } from 'axios';
import { Collision, Violation } from '../../models/collision';
import {
  fetchLawEnforcementConfiguration,
  lawEnforcementConfigurationState,
} from './lawEnforcementConfigSlice';
import { RootState } from '../store';
import { pushNotification } from './notificationSlice';
import { NotificationType } from '../../components/notifications/notification-component';
import { i } from 'vitest/dist/reporters-w_64AS5f';
import { logout } from '../slices/authSlice';

export interface Filter {
  startDate?: string;
  endDate?: string;
  locations?: string[];
  agencies: string[];
  reviewer?: string;
}

export interface lawEnforcementCollisionsState {
  collision: Collision | undefined;
  collisions: Collision[];
  isLoading: boolean;
  error: string | null;
}

const initialState: lawEnforcementCollisionsState = {
  collision: undefined,
  collisions: [],
  isLoading: false,
  error: null,
};

export const submitLECollisionToEcollision = createAsyncThunk(
  'lawEnforcementCollisions/submitLECollisionToEcollision',
  async (
    {
      csrService,
    }: {
      csrService: AxiosInstance;
    },
    { getState, rejectWithValue },
  ) => {
    const state = getState() as RootState;
    const collisionUrl = '/api/v2/law-enforcement/collisions';
    if (
      state.lawEnforcementCollisions &&
      state.lawEnforcementCollisions.collision
    ) {
      let collision: any = null;

      try {
        collision = await csrService.post(
          `${collisionUrl}/${state.lawEnforcementCollisions.collision.id}/submit/`,
          state.lawEnforcementCollisions.collision,
        );

        const returnCollision = await csrService.get(
          `${collisionUrl}/${state.lawEnforcementCollisions.collision.id}`,
        );

        return returnCollision.data;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          console.log(error);
          return rejectWithValue(error.response?.data);
        }
      }
    }
  },
);

export const publishLECollision = createAsyncThunk(
  'lawEnforcementCollisions/publishLECollision',
  async (
    {
      csrService,
    }: {
      csrService: AxiosInstance;
    },
    { getState },
  ) => {
    const state = getState() as RootState;
    const collisionUrl = '/api/v2/law-enforcement/collisions';
    if (
      state.lawEnforcementCollisions &&
      state.lawEnforcementCollisions.collision
    ) {
      let collision: any = null;

      try {
        collision = await csrService.put(
          `${collisionUrl}/${state.lawEnforcementCollisions.collision.id}`,
          state.lawEnforcementCollisions.collision,
        );

        const returnCollision = await csrService.get(
          `${collisionUrl}/${collision.data.id}`,
        );

        return returnCollision.data;
      } catch (error) {
        console.error('Error in publishCollision :', error);
        throw error;
      }
    }

    return null;
  },
);

export const updateLECollisionStatus = createAsyncThunk(
  'lawEnforcementCollisions/updateLECollisionStatus',
  async (
    {
      csrService,
      collisionId,
      status,
    }: {
      csrService: AxiosInstance;
      collisionId: string;
      status: string;
    },
    { getState },
  ) => {
    const state = getState() as RootState;
    try {
      if (
        state.lawEnforcementCollisions &&
        state.lawEnforcementCollisions.collision
      ) {
        const collisionUrl = '/api/v2/law-enforcement/collisions';

        await csrService.put(
          `/api/v2/law-enforcement/collisions/${collisionId}/status/${status}`,
        );

        const returnCollision = await csrService.get(
          `${collisionUrl}/${collisionId}`,
        );

        return returnCollision.data;
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        return Promise.reject(error!.response!.data.detail[0]);
      }
    }
  },
);

export const fetchLawEnforcementCollisionById = createAsyncThunk(
  'lawEnforcementCollisions/fetchLawEnforcementCollisionById',
  async (
    {
      collisionId,
      csrService,
    }: { collisionId: string; csrService: AxiosInstance },
    { rejectWithValue },
  ) => {
    try {
      const response = await csrService.get(
        `/api/v2/law-enforcement/collisions/${collisionId}`,
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchLawEnforcementCollisionsByAgency = createAsyncThunk(
  'lawEnforcementCollisions/fetchLawEnforcementCollisions',
  async (
    { filter, csrService }: { filter: Filter; csrService: AxiosInstance },
    { getState, dispatch, rejectWithValue },
  ) => {
    const updatedConfig = getState() as any;

    try {
      const response = await csrService.post(
        `/api/v2/law-enforcement/collisions`,
        filter,
      );

      return response.data;
    } catch (error: any) {
      console.log(
        'Error in fetchLawEnforcementCollisionsByAgency',
        error.message,
      );
      if (error.response && error.response.status) {
        if (error.response.status === 451) {
          localStorage.clear();
          sessionStorage.clear();
          dispatch(logout());
          window.location.replace("/");
        }
        // if (error.response.status === 401 || error.response.status === 403) {
        //   // As discussed, leave the user on the dashboard and don't show any data
        //   // dispatch(logout());
        //   // window.location.replace("/law/home");
        // }
        return rejectWithValue([]);
      }
      return rejectWithValue(error.message);
    }
  },
);

export const uploadCanvasImage = createAsyncThunk(
  'lawEnforcementCollisions/uploadCanvasImage',
  async (
    {
      collisionId,
      vehicleId,
      base64Image,
      csrService,
    }: {
      collisionId: string;
      vehicleId: string;
      base64Image: string;
      csrService: AxiosInstance;
    },
    { getState },
  ) => {
    const state = getState() as RootState;
    try {
      if (base64Image) {
        const response = await csrService.post(
          `/api/v2/collisions/${collisionId}/vehicles/${vehicleId}/damage-sticker-image-upload`,
          {
            base64Image: base64Image,
          },
        );

        return response.data;
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        return Promise.reject(error!.response!.data.detail[0]);
      }
    }
  },
);

const lawEnforcementConfigSlice = createSlice({
  name: 'lawEnforcementCollision',
  initialState,
  reducers: {
    clearLECollision(state) {
      state.collision = initialState.collision;

      return state;
    },
    updateLECollision(state, action: PayloadAction<Collision>) {
      state.collision = {
        ...state.collision,
        ...action.payload,
      };
      return state;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(publishLECollision.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        publishLECollision.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.collision = action.payload;
          state.isLoading = false;
          state.error = null;
        },
      )
      .addCase(
        publishLECollision.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = action.payload || 'error';
        },
      )
      .addCase(fetchLawEnforcementCollisionsByAgency.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchLawEnforcementCollisionsByAgency.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.collisions = action.payload;
          state.isLoading = false;
          state.error = null;
        },
      )
      .addCase(
        fetchLawEnforcementCollisionsByAgency.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = action.payload || 'error';
        },
      )
      .addCase(fetchLawEnforcementCollisionById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchLawEnforcementCollisionById.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.collision = action.payload;
          state.isLoading = false;
          state.error = null;
        },
      )
      .addCase(
        fetchLawEnforcementCollisionById.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = action.payload || 'error';
        },
      )
      .addCase(updateLECollisionStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        updateLECollisionStatus.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.collision = action.payload;
          state.error = null;
        },
      )
      .addCase(
        updateLECollisionStatus.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = action.payload || 'error';
        },
      )
      .addCase(submitLECollisionToEcollision.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        submitLECollisionToEcollision.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.collision = action.payload;
          state.error = null;
        },
      )
      .addCase(
        submitLECollisionToEcollision.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = action.payload || 'error';
        },
      )
      .addCase(uploadCanvasImage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        uploadCanvasImage.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.isLoading = false;
          state.error = null;
        },
      )
      .addCase(
        uploadCanvasImage.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = action.payload || 'Error uploading image';
        },
      );
  },
});

export const { updateLECollision, clearLECollision } =
  lawEnforcementConfigSlice.actions;
export default lawEnforcementConfigSlice.reducer;
